import { useState } from 'react';
import { useAlertStore } from '../../../../store/alertStore';
import { Dayjs } from 'dayjs';
import { formatDate } from '../../../../libs/dates';
import { getFlightScheduleService } from '../../../../api_services/FlightScheduleService';
import { useAppStore } from '../../../../store/appStore';
import { RescheduleRequestPayload } from '../../../../types/flightSchedule.types';
import { format as formatWithFns } from 'date-fns';
import { AxiosError } from 'axios';
import { RescheduleStatus } from '../../../../consts/RescheduleStatus';
import { makeSafeIsoDateString } from '../../../../libs/doneDate';
import { MissionCustomer, ScheduledFlight, User } from '../../../../types';
import { CheckedIcon } from '@traceair/webapp-icons';
import { White } from '@traceair/tds';

const generatePayload = (
  schedule: ScheduledFlight,
  newDate: Dayjs,
  user: User,
  reason: string | undefined,
  note: string,
): RescheduleRequestPayload => ({
  recordId: schedule.airtableRecordId,
  currentDate: formatWithFns(new Date(schedule.flightDateIso), 'yyyy-MM-dd'),
  proposedDate: newDate.format('YYYY-MM-DD'),
  initiator: 'pilot',
  initiatorPerson: user.email,
  reason: reason,
  additionalNotes: note,
  autoApprove: true,
  changeFlightDate: true,
});

const updateMission = (
  scheduleId: string,
  gcpPlacementServiceId: string | undefined,
  currentDate: string,
  proposedDate: string,
  setMissions: (missions: MissionCustomer[]) => void,
) => {
  const missions = useAppStore.getState().missions;
  if (missions) {
    missions.forEach((customer) => {
      customer.sites.forEach((site) => {
        site.scheduledFlights.forEach((schedule) => {
          if (schedule.airtableRecordId === scheduleId) {
            schedule.initialFlightDateIso = schedule.initialFlightDateIso ?? makeSafeIsoDateString(currentDate);
            schedule.flightDateIso = makeSafeIsoDateString(proposedDate);
          }
        });
        site.gcpPlacementServices.forEach((gcp) => {
          if (gcp.airtableRecordId === gcpPlacementServiceId) {
            gcp.servicesPerformedDateIso = makeSafeIsoDateString(proposedDate);
          }
        });
      });
    });
    setMissions(missions.concat());
  }
};

export const useRescheduleFlight = (
  closeModal: () => void,
  newDate: Dayjs | undefined,
  reason: string | undefined,
  note: string,
) => {
  const { selectedDateChangeMission, user, setMissions } = useAppStore();
  const { addAlert } = useAlertStore();

  const [isSubmittingRescheduleRequest, setIsSubmittingRescheduleRequest] = useState(false);

  const customer = selectedDateChangeMission?.mission.customer;
  const site = selectedDateChangeMission?.mission.site;
  const schedule = selectedDateChangeMission?.mission.schedule;
  const gcpPlacementService = selectedDateChangeMission?.mission.gcpPlacementService;

  const rescheduleFlight = async () => {
    if (isSubmittingRescheduleRequest || !schedule || !customer || !site || !newDate || !user) return;
    setIsSubmittingRescheduleRequest(true);

    const newDateFormatted = formatDate({ date: newDate.toDate() });
    const currentDateFormatted = formatDate({ date: new Date(schedule.flightDateIso) });

    const payload = generatePayload(schedule, newDate, user, reason, note);

    try {
      const result = await getFlightScheduleService().createRescheduleRequest(customer.id, site.id, payload);

      if (result.reschedule_status !== RescheduleStatus.Accepted) {
        addAlert({
          type: 'warning-accent',
          title: 'Failed to reschedule the flight',
          description: 'The server responded with status: ' + result.reschedule_status,
        });
        closeModal();
        return;
      }

      updateMission(
        result.scan_id,
        gcpPlacementService?.airtableRecordId,
        payload.currentDate,
        payload.proposedDate,
        setMissions,
      );
    } catch (error) {
      addAlert({
        type: 'warning-accent',
        title: 'Failed to reschedule the flight',
        description: (error as AxiosError).message,
      });
      closeModal();
      return;
    }

    addAlert({
      title: `${site.name} flight has been moved from ${currentDateFormatted} to ${newDateFormatted}`,
      icon: <CheckedIcon size={24} color={White} />,
      type: 'neutral-high-accent',
    });
    closeModal();
  };

  return {
    isSubmittingRescheduleRequest,
    rescheduleFlight,
  };
};
